import dayjs from 'dayjs';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { UseQueryState } from 'urql';
import useDebouncedFunc from 'hooks/useDebouncedFunc';
import useOfflineQuery from 'hooks/useOfflineQuery';
import useProfileConfig from 'hooks/useProfileConfig';
import { useReportsDashboardURLParams } from 'hooks/useReportsDashboardURLParams';
import { PricingDashboardNewQuery, usePricingDashboardNewQuery } from 'generated/graphql';
import { DEBOUNCE_TIME, WeekToMonthOptions } from '../reports.utils';
import { useIsReportsPdfMode } from '../useIsReportsPdfMode';
import { useReportsDashboard } from '../useReportsDashboard';
import { useReportsDashboardFilters } from '../useReportsDashboardFilters';

interface PricingDashboardContextProps {
  pricingDashboardNewResult: UseQueryState<PricingDashboardNewQuery>;
  pricingDashboardFetching: boolean;
  refreshPricingDashboardNewResult: () => void;
  weekToMonthOption: WeekToMonthOptions;
  setWeekToMonthOption: (weekToMonthOption: WeekToMonthOptions) => void;
  showPricingGraphs: boolean;
  setShowPricingGraphs: (showPricingGraphs: boolean) => void;
  viewPricingCalendar: boolean;
  setViewPricingCalendar: (viewPricingCalendar: boolean) => void;
  handleWeekToMonthSelected: (value: string) => void;
}

const Context = createContext<PricingDashboardContextProps | undefined>(undefined);

export const PricingDashboardProvider = ({ children }: { children: ReactNode }) => {
  const { profileID } = useProfileConfig();

  // Reports Providers
  const { reportsDashboardNewQueryInput, reportsDashboardState, setReportsDashboardState } = useReportsDashboard();
  const { handleFilter } = useReportsDashboardFilters();
  const { productID, productFilterInputs, removeProductIDQueryParam } = useReportsDashboardURLParams();
  const isReportsPdfMode = useIsReportsPdfMode();

  // Pricing Dashboard States
  const [weekToMonthOption, setWeekToMonthOption] = useState(WeekToMonthOptions.WEEK_TO_DATE);
  const [showPricingGraphs, setShowPricingGraphs] = useState(false);
  const [viewPricingCalendar, setViewPricingCalendar] = useState(false);

  const [pricingDashboardNewResult, refreshPricingDashboardNewResult] = useOfflineQuery(
    'pricing-dashboard-new-query',
    usePricingDashboardNewQuery,
    {
      variables: { input: reportsDashboardNewQueryInput },
      requestPolicy: 'network-only',
      pause: isReportsPdfMode,
    },
  );

  const pricingDashboardFetching = (pricingDashboardNewResult.fetching || pricingDashboardNewResult.stale) ?? false;

  const handleWeekToMonthSelected = useDebouncedFunc((value: string) => {
    setWeekToMonthOption(value as WeekToMonthOptions);
    const timePeriod = value === WeekToMonthOptions.WEEK_TO_DATE ? 'week' : 'month';
    setReportsDashboardState((prev) => ({
      ...prev,
      endDate: dayjs(),
      startDate: dayjs().subtract(1, timePeriod),
    }));
  }, DEBOUNCE_TIME);

  /*This useEffect is required for the compare button in the pricing table.
    The compare button updates the filters with the relevant params 
    and changes the pricing dash view to the graphs*/
  useEffect(
    function handlePricingTableCompare() {
      if (isReportsPdfMode || profileID === '0' || productID === 0) {
        return;
      }
      handleFilter(reportsDashboardState.searchValue, productFilterInputs);
      setShowPricingGraphs(true);
      removeProductIDQueryParam();
    },
    [
      isReportsPdfMode,
      profileID,
      productID,
      reportsDashboardState.searchValue,
      productFilterInputs,
      handleFilter,
      removeProductIDQueryParam,
    ],
  );

  const value = useMemo<PricingDashboardContextProps>(
    () => ({
      pricingDashboardNewResult,
      pricingDashboardFetching,
      refreshPricingDashboardNewResult,
      weekToMonthOption,
      setWeekToMonthOption,
      showPricingGraphs,
      setShowPricingGraphs,
      viewPricingCalendar,
      setViewPricingCalendar,
      handleWeekToMonthSelected,
    }),
    [
      pricingDashboardNewResult,
      pricingDashboardFetching,
      refreshPricingDashboardNewResult,
      weekToMonthOption,
      setWeekToMonthOption,
      showPricingGraphs,
      setShowPricingGraphs,
      viewPricingCalendar,
      setViewPricingCalendar,
      handleWeekToMonthSelected,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const usePricingDashboard = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('usePricingDashboard must be used within a PricingDashboardProvider');
  }
  return result;
};
